<template>
  <v-container fluid>
    <h2>{{ $tt('feed.mapSets') }}</h2>
    <p class="ml-2 mb-2 caption grey--text">{{ $tt('feed.mapSetDescription') }}</p>

    <v-layout align-center class="ml-2 mb-2">
      <v-btn small color="primary" outlined @click="openEditor(null)">
        <v-icon small left>mdi-plus</v-icon>
        {{ $tt('feed.mapSetCreate') }}
      </v-btn>
      <v-btn small text color="primary" class="ml-2" @click="openFieldAttrs()">
        <v-icon small left>mdi-tag-outline</v-icon>
        {{ $tt('feed.fieldAttrsTitle') }}
      </v-btn>
    </v-layout>

    <v-row v-if="sets.length" dense class="mb-3">
      <v-col cols="12" md="5" lg="4" class="list-pane">
        <v-simple-table dense class="list-scroll">
          <tbody>
            <tr
              v-for="s in pagedSets"
              :key="s.id"
              :class="{ 'selected-set-row': selectedSet && selectedSet.id === s.id }"
              style="cursor: pointer"
              @click="selectSet(s)"
            >
              <td class="py-2">
                <strong>{{ s.name }}</strong>
                <div class="caption">{{ $tt('feed.mapSetFields') }}: {{ s.num_fields }}</div>
              </td>
              <td class="py-2">
                <div v-if="setCreatedDate(s)" class="caption grey--text">
                  {{ $tt('DocFormFieldRegisteredDateFormatBase', { createDate: setCreatedDate(s) }) }}
                </div>
                <div v-if="setValidFrom(s)" class="caption grey--text">
                  {{ $tt('feed.fieldValidFromDate', { date: setValidFrom(s) }) }}
                </div>
                <div v-if="setValidTo(s)" class="caption grey--text">
                  {{ $tt('feed.fieldValidUntil', { date: setValidTo(s) }) }}
                </div>
              </td>
              <td class="text-right pr-1" style="width: 40px">
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn small icon v-bind="attrs" v-on="on">
                      <v-icon small>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="openEditor(s)">
                      <v-list-item-title>{{ $tt('feed.vraEditMap') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="deleteTarget = s">
                      <v-list-item-title class="error--text">{{ $tt('customDelete') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <list-pager-component :total="sets.length" :page.sync="setsPage" :page-size.sync="setsPageSize" />
      </v-col>
      <v-col cols="12" md="7" lg="8">
        <!-- セットをクリックすると構成圃場をマップ上で強調表示 -->
        <fields-map-component :fields="storeFields" :highlighted-pks="selectedSetFieldPks" height="75vh" />
      </v-col>
    </v-row>
    <p v-else class="ml-2 caption grey--text">{{ $tt('feed.fieldSetEmpty') }}</p>

    <!-- 圃場セット作成/編集 (フィルタのみ。VRA パラメータは持たない) -->
    <v-dialog v-model="editorDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="subtitle-1">{{
          editTarget ? $tt('feed.vraEditMap') : $tt('feed.mapSetCreate')
        }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.name" dense :label="$tt('feed.workName')" />

          <!-- フィルタ: 圃場の有効期間 (時系列) + 作目 + 名称/メモ -->
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model="form.validFrom"
                type="date"
                dense
                hide-details
                clearable
                :label="$tt('feed.fieldValidFrom')"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.validTo"
                type="date"
                dense
                hide-details
                clearable
                :label="$tt('feed.fieldValidTo')"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="form.crop" dense hide-details :label="$tt('feed.mapSetCrop')" />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="form.namePattern" dense hide-details :label="$tt('feed.mapSetNameContains')" />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="form.memoPattern" dense hide-details :label="$tt('feed.mapSetMemoContains')" />
            </v-col>
          </v-row>

          <!-- プレビュー -->
          <div class="caption grey--text mt-3 mb-1">
            {{ $tt('feed.mapSetPreviewCount') }}: {{ matchedFields.length }}
          </div>
          <div class="fieldset-preview">
            <div v-if="!matchedFields.length" class="caption grey--text pa-2">{{ $tt('feed.mapSetNoMatch') }}</div>
            <v-checkbox
              v-for="f in matchedFields"
              :key="f.pk"
              dense
              hide-details
              class="mt-0"
              :input-value="!form.excluded.includes(String(f.pk))"
              :label="f.name + attrLabel(f)"
              @change="toggleExclude(f)"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="editorDialog = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!form.name" @click="saveSet()">
            {{ $tt('customSaveAction') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 圃場属性 (有効期間・作目) -->
    <v-dialog v-model="attrsDialog" max-width="640">
      <v-card>
        <v-card-title class="subtitle-1">{{ $tt('feed.fieldAttrsTitle') }}</v-card-title>
        <v-card-text style="max-height: 60vh; overflow-y: auto">
          <v-row dense v-for="f in attrEditableFields" :key="f.pk" align="center">
            <v-col cols="3" class="caption text-truncate">{{ f.name }}</v-col>
            <v-col cols="3">
              <v-text-field
                v-model="attrsEdit[f.pk].valid_from"
                type="date"
                dense
                hide-details
                :label="$tt('feed.fieldValidFrom')"
                clearable
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="attrsEdit[f.pk].valid_to"
                type="date"
                dense
                hide-details
                :label="$tt('feed.fieldValidTo')"
                clearable
              />
            </v-col>
            <v-col cols="3">
              <v-text-field v-model="attrsEdit[f.pk].crop" dense hide-details :label="$tt('feed.mapSetCrop')" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="attrsSaving" @click="attrsDialog = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" :loading="attrsSaving" @click="saveAttrs()">{{ $tt('customSaveAction') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 削除確認 -->
    <v-dialog :value="!!deleteTarget" max-width="380" @input="deleteTarget = null">
      <v-card>
        <v-card-text class="pt-4">{{ $tt('feed.mapSetDeleteConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteTarget = null">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="error" text @click="deleteSet()">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import feedApiClient from '@/lib/feed-api-client'
import { buildFieldSetFilter, evaluateFieldSetFilter, resolveFieldPolygons } from '@/lib/field-set-filter'
import Toast from '@/lib/toast'
import FieldsMapComponent from '@/components/fields/FieldsMapComponent'
import { emitFeedEvent } from '@/lib/feed-events'
import ListPagerComponent from '@/components/ListPagerComponent'

export default {
  name: 'FieldSets',
  components: { FieldsMapComponent, ListPagerComponent },
  data() {
    return {
      sets: [],
      setsPage: 1,
      setsPageSize: 20,
      selectedSet: null,
      attrs: {},
      editorDialog: false,
      editTarget: null,
      form: this.emptyForm(),
      saving: false,
      attrsDialog: false,
      attrsEdit: {},
      attrsSaving: false,
      deleteTarget: null,
    }
  },
  computed: {
    storeFields() {
      return this.$store.getters.fields
    },
    // 属性編集欄の初期化が済んだ圃場のみ (圃場一覧の非同期ロードとの競合を避ける)
    attrEditableFields() {
      return this.storeFields.filter(f => this.attrsEdit[f.pk])
    },
    matchedFields() {
      return evaluateFieldSetFilter(this.storeFields, this.attrs, this.buildFilter(), { ignoreExcluded: true })
    },
    pagedSets() {
      const start = (this.setsPage - 1) * this.setsPageSize
      return this.sets.slice(start, start + this.setsPageSize)
    },
    // 選択中セットの構成圃場 pk (マップ強調表示用)
    selectedSetFieldPks() {
      if (!this.selectedSet) {
        return []
      }
      return evaluateFieldSetFilter(this.storeFields, this.attrs, this.selectedSet.filter).map(f => String(f.pk))
    },
  },
  watch: {
    // 圃場一覧はストアで非同期ロードされるため、届いたら属性編集欄を作り直す
    storeFields() {
      this.initAttrsEdit()
    },
  },
  mounted() {
    this.loadSets()
    this.loadAttrs()
  },
  methods: {
    selectSet(set) {
      this.selectedSet = this.selectedSet && this.selectedSet.id === set.id ? null : set
    },
    emptyForm() {
      return {
        name: '',
        validFrom: '',
        validTo: '',
        crop: '',
        namePattern: '',
        memoPattern: '',
        excluded: [],
      }
    },
    // メンバー圃場群を MultiPolygon にまとめる (フィード表示用)
    multiPolygonOf(fields) {
      const coords = (fields || []).map(f => f.polygon && f.polygon.coordinates).filter(Boolean)
      return coords.length ? { type: 'MultiPolygon', coordinates: coords } : undefined
    },
    setCreatedDate(s) {
      return s.created_at ? String(s.created_at).slice(0, 10) : null
    },
    // セットの有効期間 = フィルタの期間 (対象日指定の場合は開始・終了とも同日)
    setValidFrom(s) {
      const p = (s.filter || {}).period || {}
      return p.from || p.at || null
    },
    setValidTo(s) {
      const p = (s.filter || {}).period || {}
      return p.to || p.at || null
    },
    filterLabel(filter) {
      const f = filter || {}
      const parts = []
      if (f.period && (f.period.at || f.period.from))
        parts.push(f.period.at || f.period.from + '〜' + (f.period.to || ''))
      if (f.crop) parts.push(f.crop)
      if (f.name_pattern) parts.push('"' + f.name_pattern + '"')
      return parts.join(' / ')
    },
    async loadSets() {
      try {
        const res = await feedApiClient.fieldSets()
        this.sets = res.data.records || []
      } catch (e) {
        console.error(e.response || e)
      }
    },
    async loadAttrs() {
      try {
        const res = await feedApiClient.fieldAttrs()
        this.attrs = res.data.attrs || {}
        this.initAttrsEdit()
      } catch (e) {
        console.error(e.response || e)
      }
    },
    initAttrsEdit() {
      const edit = {}
      for (const f of this.storeFields) {
        const a = this.attrs[String(f.pk)] || {}
        edit[f.pk] = { valid_from: a.valid_from || '', valid_to: a.valid_to || '', crop: a.crop || '' }
      }
      this.attrsEdit = edit
    },
    attrLabel(f) {
      const a = this.attrs[String(f.pk)] || {}
      const parts = []
      if (a.crop) parts.push(a.crop)
      if (a.valid_from || a.valid_to) parts.push((a.valid_from || '') + '〜' + (a.valid_to || ''))
      return parts.length ? ' (' + parts.join(' ') + ')' : ''
    },
    toggleExclude(f) {
      const id = String(f.pk)
      const i = this.form.excluded.indexOf(id)
      if (i >= 0) {
        this.form.excluded.splice(i, 1)
      } else {
        this.form.excluded.push(id)
      }
    },
    openFieldAttrs() {
      this.initAttrsEdit()
      this.attrsDialog = true
    },
    async saveAttrs() {
      this.attrsSaving = true
      try {
        for (const f of this.storeFields) {
          const e = this.attrsEdit[f.pk]
          const a = this.attrs[String(f.pk)] || {}
          if (
            (e.valid_from || '') !== (a.valid_from || '') ||
            (e.valid_to || '') !== (a.valid_to || '') ||
            (e.crop || '') !== (a.crop || '')
          ) {
            await feedApiClient.putFieldAttrs(String(f.pk), {
              valid_from: e.valid_from || null,
              valid_to: e.valid_to || null,
              crop: e.crop || null,
            })
          }
        }
        await this.loadAttrs()
        this.attrsDialog = false
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.saveFailed')
      } finally {
        this.attrsSaving = false
      }
    },
    openEditor(s) {
      this.editTarget = s
      if (s) {
        const filter = s.filter || {}
        const period = filter.period || {}
        this.form = {
          name: s.name,
          // 旧形式 (対象日 at) は開始・終了に展開して編集する
          validFrom: period.from || period.at || '',
          validTo: period.to || period.at || '',
          crop: filter.crop || '',
          namePattern: filter.name_pattern || '',
          memoPattern: filter.memo_pattern || '',
          excluded: (filter.exclude_field_ids || []).slice(),
        }
      } else {
        this.form = this.emptyForm()
      }
      this.editorDialog = true
    },
    buildFilter() {
      return buildFieldSetFilter(this.form)
    },
    resolvedFields() {
      const filter = this.buildFilter()
      return resolveFieldPolygons(evaluateFieldSetFilter(this.storeFields, this.attrs, filter))
    },
    async saveSet() {
      this.saving = true
      try {
        const body = { name: this.form.name, filter: this.buildFilter() }
        if (this.editTarget) {
          await feedApiClient.updateFieldSet(this.editTarget.id, body)
          await feedApiClient.replaceFieldSetFields(this.editTarget.id, this.resolvedFields())
        } else {
          body.fields = this.resolvedFields()
          if (!body.fields.length) {
            Toast.i18nError('feed.mapSetNoMatch')
            return
          }
          await feedApiClient.createFieldSet(body)
          emitFeedEvent('field_set_added', this.form.name, undefined, this.multiPolygonOf(body.fields))
        }
        this.editorDialog = false
        this.loadSets()
      } catch (e) {
        console.error(e.response || e)
        Toast.i18nError('feed.saveFailed')
      } finally {
        this.saving = false
      }
    },
    async deleteSet() {
      const s = this.deleteTarget
      if (!s) return
      try {
        await feedApiClient.deleteFieldSet(s.id)
        this.sets = this.sets.filter(x => x.id !== s.id)
      } catch (e) {
        console.error(e.response || e)
      } finally {
        this.deleteTarget = null
      }
    },
  },
}
</script>

<style scoped>
.fieldset-preview {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 8px;
}

/* リスト列: マップと同じ高さで、テーブルはスクロール・ページャーは最下段に固定 */
.list-pane {
  display: flex;
  flex-direction: column;
  height: 75vh;
}
.list-pane .list-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
}
</style>
